import React from "react";
import { Request } from "../../hooks/Request";
import { useDialog } from "../../contexts/DialogContext";
import { requestAuth } from "../../components/services/RequestService";
import { toast } from "react-toastify";
import Circle from "../../components/Animation/Circle";
import { TransportContractPdf } from "./TransportContractPdf";

const listFields = [
  "subastaDeTransporte",
  "antecedentesContratoDeTransporte",
  "tipoServicio",
  "objetoContratoDeTransporte",
  "diasPlazo",
  "diasPlazoAduana",
  "precioMonto",
  "responsableDeAlmacen",
  "cuidadDeLaNotaria",
  "fechaContrato",
  "tipoDeImportacion",
  "cantidad",
  "modalidadCargaTransporte",
  "modalidadDeCarga",
  "descripciónGeneralDeLaCompra",
  "proveedor",
  "puertoDeDescarga",
  "puertoOCiudadDeEmbarque",
  "paísDeDescarga",
  "ciudadDestinoFinal",
];

const DATA_VALUES_FIELDS = [
  "subastaDeTransporte",
  "company",
  "procNumber",
  "tipoDeImportacion",
  "cantidad",
  "modalidadCargaTransporte",
  "modalidadDeCarga",
  "almacénDestino",
  "descripciónGeneralDeLaCompra",
  "proveedor",
  "puertoDeDescarga",
]
  .map((f) => `fieldnames=${f}`)
  .join("&");

const DATASET_QUERY = listFields.map((f) => `fieldnames=${f}`).join("&");

// Campos del objeto parentProcedure (props) que sirven como respaldo.
// OJO: procNumber NO se hereda, el número del contrato es del subtrámite.
const PARENT_PROPS_FALLBACK = [
  "proveedor",
  "descripciónGeneralDeLaCompra",
  "almacénDestino",
  "cantidad",
  "modalidadDeCarga",
  "tipoDeImportacion",
];
const isEmpty = (v) =>
  v === undefined ||
  v === null ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0);

// Helper functions for Number to Words in Spanish (identical to TransportContractPdf)
const Unidades = (num) => {
  switch (num) {
    case 1:
      return "un";
    case 2:
      return "dos";
    case 3:
      return "tres";
    case 4:
      return "cuatro";
    case 5:
      return "cinco";
    case 6:
      return "seis";
    case 7:
      return "siete";
    case 8:
      return "ocho";
    case 9:
      return "nueve";
    default:
      return "";
  }
};

const Decenas = (num) => {
  const decena = Math.floor(num / 10);
  const unidad = num - decena * 10;
  switch (decena) {
    case 1:
      switch (unidad) {
        case 0:
          return "diez";
        case 1:
          return "once";
        case 2:
          return "doce";
        case 3:
          return "trece";
        case 4:
          return "catorce";
        case 5:
          return "quince";
        default:
          return "dieci" + Unidades(unidad);
      }
    case 2:
      switch (unidad) {
        case 0:
          return "veinte";
        default:
          return "veinti" + Unidades(unidad);
      }
    case 3:
      return DecenasY("treinta", unidad);
    case 4:
      return DecenasY("cuarenta", unidad);
    case 5:
      return DecenasY("cincuenta", unidad);
    case 6:
      return DecenasY("sesenta", unidad);
    case 7:
      return DecenasY("setenta", unidad);
    case 8:
      return DecenasY("ochenta", unidad);
    case 9:
      return DecenasY("noventa", unidad);
    case 0:
      return Unidades(unidad);
    default:
      return "";
  }
};

const DecenasY = (strSin, numUnidades) => {
  return numUnidades > 0 ? strSin + " y " + Unidades(numUnidades) : strSin;
};

const Centenas = (num) => {
  const centenas = Math.floor(num / 100);
  const decenas = num - centenas * 100;
  switch (centenas) {
    case 1:
      return decenas > 0 ? "ciento " + Decenas(decenas) : "cien";
    case 2:
      return "doscientos " + Decenas(decenas);
    case 3:
      return "trescientos " + Decenas(decenas);
    case 4:
      return "cuatrocientos " + Decenas(decenas);
    case 5:
      return "quinientos " + Decenas(decenas);
    case 6:
      return "seiscientos " + Decenas(decenas);
    case 7:
      return "setecientos " + Decenas(decenas);
    case 8:
      return "ochocientos " + Decenas(decenas);
    case 9:
      return "novecientos " + Decenas(decenas);
    default:
      return Decenas(decenas);
  }
};

const Seccion = (num, divisor, strSingular, strPlural) => {
  const cientos = Math.floor(num / divisor);
  let letras = "";
  if (cientos > 0) {
    letras = cientos > 1 ? Centenas(cientos) + " " + strPlural : strSingular;
  } else {
    letras = strSingular;
  }
  return letras;
};

const Miles = (num) => {
  const divisor = 1000;
  const cientos = Math.floor(num / divisor);
  const resto = num - cientos * divisor;
  const strMiles = Seccion(num, divisor, "un mil", "mil");
  const strCentenas = Centenas(resto);
  return strMiles === "" || cientos === 0
    ? strCentenas
    : strMiles + (strCentenas ? " " + strCentenas : "");
};

const Millones = (num) => {
  const divisor = 1000000;
  const cientos = Math.floor(num / divisor);
  const resto = num - cientos * divisor;
  const strMillones = Seccion(num, divisor, "un millón", "millones");
  const strMiles = Miles(resto);
  return strMillones === "" || cientos === 0
    ? strMiles
    : strMillones + (strMiles ? " " + strMiles : "");
};

const NumeroALetras = (num) => {
  if (!num || isNaN(num)) return "cero 00/100";
  const data = {
    numero: num,
    enteros: Math.floor(num),
    centavos: Math.round(num * 100) - Math.floor(num) * 100,
  };
  if (data.enteros === 0) return "cero 00/100";
  return (
    Millones(data.enteros) +
    ` ${data.centavos === 0 ? "00" : data.centavos}/100`
  );
};

const WAREHOUSE_MANAGERS = {
  "SANTIVAÑEZ, COCHABAMBA": "WILDER ORELLANA",
  "BLANCO GALINDO, COCHABAMBA": "MARCELO TORREZ",
  "SEGEA, ORURO": "MARCELO TORREZ",
};

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return "";
  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  const partes = fechaStr.split("-");
  if (partes.length !== 3) return fechaStr;
  return `${partes[2].substring(0, 2)} de ${meses[parseInt(partes[1]) - 1]} de ${partes[0]}`;
};

export const FormTransportContract = (props) => {
  const { procedure, parentProcedure } = props;
  const { openDialog } = useDialog();

  const procedureId = procedure.id || procedure.procedureId;
  const { data: ownDatasetData, loading: datasetLoaded } = Request({
    urlApi: `/Procedure/${procedureId}/datasets?${DATASET_QUERY}`,
  });

  const { data: ownDataValues, loading: valuesLoaded } = Request({
    urlApi: `/Procedure/${procedureId}/dataValues?${DATA_VALUES_FIELDS}`,
  });

  const { data: procedureDetail } = Request({
    urlApi: `/Procedure/${procedureId}/Detail`,
  });

  const parentId =
    (typeof parentProcedure === "object"
      ? (parentProcedure?.id ?? parentProcedure?.procedureId)
      : parentProcedure) ||
    procedure.fatherProcedureId ||
    procedure.parentProcedureId ||
    procedureDetail?.fatherProcedureId ||
    null;
  const { data: parentDataValues } = Request({
    urlApi: parentId
      ? `/Procedure/${parentId}/dataValues?${DATA_VALUES_FIELDS}`
      : null,
  });

  const { data: parentDatasetData } = Request({
    urlApi: parentId
      ? `/Procedure/${parentId}/datasets?${DATASET_QUERY}`
      : null,
  });

  const { data: parentDetail } = Request({
    urlApi: parentId ? `/Procedure/${parentId}/Detail` : null,
  });
  const dataValues = React.useMemo(() => {
    const merged = {};

    if (parentProcedure && typeof parentProcedure === "object") {
      PARENT_PROPS_FALLBACK.forEach((key) => {
        if (!isEmpty(parentProcedure[key])) merged[key] = parentProcedure[key];
      });
    }

    Object.entries(parentDataValues || {}).forEach(([key, value]) => {
      if (key === "procNumber") return; // no heredar
      if (!isEmpty(value)) merged[key] = value;
    });

    Object.entries(ownDataValues || {}).forEach(([key, value]) => {
      if (!isEmpty(value)) merged[key] = value;
    });

    return merged;
  }, [ownDataValues, parentDataValues, parentProcedure]);

  const datasetData = React.useMemo(() => {
    if (!ownDatasetData) return ownDatasetData;
    return ownDatasetData.map((field) => {
      if (!isEmpty(field.initial)) return field;
      const fromParent = parentDatasetData?.find((p) => p.name === field.name);
      return isEmpty(fromParent?.initial)
        ? field
        : { ...field, initial: fromParent.initial };
    });
  }, [ownDatasetData, parentDatasetData]);
  const waitingParent = !!parentId && (!parentDataValues || !parentDatasetData);
  const capitalize = (text) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

  const numRef =
    procedureDetail?.numRef ||
    parentDetail?.numRef ||
    parentProcedure?.codigoDeInterno ||
    "(S/N)";
  const proveedor = dataValues?.proveedor || "(SIN PROVEEDOR)";
  const descripcionGeneralDeLaCompra =
    dataValues?.descripciónGeneralDeLaCompra?.trim() || "";
  const puertoDescarga =
    capitalize(
      datasetData?.find((item) => item.name === "puertoDeDescarga")?.initial,
    ) || "(SIN PUERTO DE DESCARGA)";

  const paisDescarga =
    capitalize(
      datasetData?.find((item) => item.name === "paísDeDescarga")?.initial,
    ) || "(SIN PAÍS DE DESCARGA)";

  const quantity =
    JSON.parse(dataValues?.cantidadSubDatas?.[0]?.description2 || "{}")
      .quantity ?? 0;

  const containerType =
    JSON.parse(dataValues?.cantidadSubDatas?.[0]?.description2 || "{}")
      .containerType ?? 0;

  const modalidadDeCarga = dataValues?.modalidadDeCarga;

  const tipoImportacion = dataValues?.tipoDeImportacion || "";

  // Load providers from options dropdown
  const { data: providersList, loading: providersLoaded } = Request({
    urlApi: `/Dropdown/providers/options`,
  });

  const companyId = procedure?.companyId || parentProcedure?.companyId;
  const { data: companyInfo } = Request({
    urlApi: companyId ? `/company/${companyId}` : null,
  });

  const { data: companyDetails } = Request({
    urlApi: companyId ? `/company/${companyId}/details` : null,
  });

  // State to hold the live values of editable fields
  const [formValues, setFormValues] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  // Initialize formValues when data is loaded
  React.useEffect(() => {
    if (datasetLoaded && datasetData) {
      // Precio ya cotizado y aprobado en "Cotizar Transporte" — no se vuelve a pedir.
      const winningQuote = dataValues?.subastaDeTransporteSubDatas?.find(
        (s) => String(s.description3).toLowerCase() === "true",
      );

      const initial = {};
      datasetData.forEach((field) => {
        let val = field.initial;
        if (!val) {
          if (field.name === "tipoServicio") val = "exclusivo internacional";
          else if (field.name === "diasPlazo") val = "7";
          else if (field.name === "diasPlazoAduana") val = "5";
          else if (field.name === "precioMonto" && winningQuote?.numberValue) {
            const qty = parseFloat(dataValues?.cantidad) || 1;
            val = String(Number(winningQuote.numberValue) * qty);
          } else if (field.name === "cuidadDeLaNotaria") val = "Cochabamba";
          else if (field.name === "fechaContrato") {
            const today = new Date();
            val = today.toISOString().split("T")[0];
          } else {
            val = "";
          }
        }
        initial[field.name] = val;
      });

      if (!initial["antecedentesContratoDeTransporte"] && tipoImportacion) {
        initial["antecedentesContratoDeTransporte"] = tipoImportacion;
      }

      // Auto-fill objetoContratoDeTransporte from cantidad + modalidad + descripción
      if (!initial["objetoContratoDeTransporte"]) {
        const parts = [
          dataValues?.cantidad,
          dataValues?.modalidadCargaTransporte || dataValues?.modalidadDeCarga,
          dataValues?.descripciónGeneralDeLaCompra,
        ].filter(Boolean);
        if (parts.length > 0) {
          initial["objetoContratoDeTransporte"] = parts.join(" - ");
        }
      }

      if (!initial["responsableDeAlmacen"]) {
        const warehouse = dataValues?.["almacénDestino"] || "";
        initial["responsableDeAlmacen"] = WAREHOUSE_MANAGERS[warehouse] || "";
      }

      setFormValues(initial);
    }
  }, [datasetLoaded, datasetData, dataValues, tipoImportacion]);

  // Handle live edits
  const handleChange = (name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit current values to the dataset endpoint
  const handleSave = async (showPdf = false) => {
    setSaving(true);
    try {
      const payload = datasetData.map((field) => {
        return {
          Value:
            formValues[field.name] !== undefined
              ? String(formValues[field.name])
              : "",
          ProcedureId: procedureId,
          FieldId: field.id,
          name: field.name,
          url: field.url || "",
          type: field.type || "text",
        };
      });

      await requestAuth("post", "/dataset", payload);
      toast.success("Contrato guardado exitosamente");

      if (showPdf) {
        openDialog(
          <TransportContractPdf
            procedureId={procedureId}
            companyId={companyId}
            parentProcedure={props.parentProcedure}
          />,
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar el contrato");
    } finally {
      setSaving(false);
    }
  };

  if (
    !datasetLoaded ||
    !valuesLoaded ||
    !providersLoaded ||
    !ownDatasetData ||
    !ownDataValues ||
    waitingParent
  ) {
    return <Circle />;
  }

  // Determine selected provider and company details
  const selectedTransportItem = dataValues?.subastaDeTransporteSubDatas?.find(
    (s) => String(s.description3).toLowerCase() === "true",
  );
  const providerName = selectedTransportItem ? selectedTransportItem.label : "";
  const providerDetail = providersList?.find((p) => p.name === providerName);
  const providerData = providerDetail?.data
    ? JSON.parse(providerDetail.data)
    : {};

  const company = dataValues.company || companyInfo || {};
  const companyDetailsMap = {};
  if (Array.isArray(companyDetails)) {
    companyDetails.forEach((d) => {
      if (d.value && d.value !== "[]" && d.value !== "{}") {
        companyDetailsMap[d.label] = d.value;
      }
    });
  }
  const companyName = company.razonSocial || "PLASTIFORTE S.R.L.";
  const companySigla = company.sigla || "PLASTIFORTE";
  const isPlastiforte = [companyName, companySigla].some((value) =>
    String(value || "")
      .toUpperCase()
      .includes("PLASTIFORTE"),
  );
  const legalRepresentativeSignatureSrc = isPlastiforte
    ? "/images/firma-representante-plastiforte.png"
    : "";

  const companyTestimonio =
    company.testimonio || companyDetailsMap["testimonio"] || " N-97/2025";
  const companyFundempresa =
    company.fundempresa || companyDetailsMap["fundempresa"] || "SEPREC";

  const isWallhaven = companyName.includes("WALLHAVEN");
  const logoSrc =
    company.logoUrl ||
    (isWallhaven
      ? "/images/logo-Wallhaven.png"
      : "/images/logo-Plastiforte-v2.png");

  // Helper renderer for inline input
  const renderInlineInput = (
    name,
    placeholder,
    type = "text",
    widthClass = "w-40",
  ) => {
    return (
      <input
        type={type}
        placeholder={placeholder}
        value={formValues[name] || ""}
        onChange={(e) => handleChange(name, e.target.value)}
        className={`inline-block border-b-2 border-dashed border-amber-400 focus:border-amber-600 focus:border-solid bg-yellow-100/70 focus:bg-transparent outline-none font-bold text-gray-900 px-2 py-0.5 text-center rounded transition ${widthClass}`}
      />
    );
  };

  // Helper renderer for inline textarea
  const renderInlineTextarea = (name, placeholder) => {
    return (
      <textarea
        placeholder={placeholder}
        value={formValues[name] || ""}
        onChange={(e) => handleChange(name, e.target.value)}
        rows={3}
        className="w-full mt-2 mb-2 border-2 border-dashed border-amber-300 focus:border-amber-600 focus:border-solid bg-yellow-50/50 focus:bg-transparent outline-none font-medium text-gray-900 p-2.5 rounded transition text-justify resize-y shadow-sm"
      />
    );
  };

  return (
    <div className="flex flex-col bg-gray-100 min-h-[90vh] rounded-lg overflow-hidden shadow-2xl">
      {/* Upper Sticky Toolbar */}
      <div className="sticky top-0 z-50 flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200 shadow-md">
        <div>
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <i className="fa-solid fa-file-signature text-amber-500"></i>
            Editor de Contrato interactivo (Edición en Vivo)
          </h2>
          <p className="text-xs text-gray-500">
            Edita las áreas destacadas en azul directamente sobre el texto del
            contrato.
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="px-4 py-2 bg-gray-150 hover:bg-gray-200 text-gray-700 font-semibold text-sm rounded shadow-sm transition duration-150 disabled:opacity-50 flex items-center gap-1.5"
          >
            <i className="fa-solid fa-floppy-disk text-gray-500"></i>
            {saving ? "Guardando..." : "Guardar Borrador"}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm rounded shadow-sm transition duration-150 disabled:opacity-50 flex items-center gap-1.5"
          >
            <i className="fa-solid fa-file-pdf"></i>
            Guardar y Previsualizar PDF
          </button>
        </div>
      </div>

      {/* Contract physical viewport sheet */}
      <div className="flex-1 py-8 px-4 overflow-y-auto max-h-[80vh]">
        <div className="max-w-[850px] mx-auto bg-white border border-gray-300 shadow-2xl rounded-sm p-16 font-serif text-gray-800 leading-relaxed text-[13.5px] text-justify">
          {/* Header */}
          <div className="flex justify-between items-start border-b border-gray-200 pb-4 mb-6">
            <img
              src={logoSrc}
              alt="Logo"
              className="w-[140px] object-contain"
            />
            <div className="text-right text-[10px] font-sans text-gray-500">
              <div className="font-bold text-gray-800">
                N° {dataValues.procNumber || "438"}
              </div>
              <div>PR-COM-121-R201 v2</div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-center font-bold text-sm tracking-wide leading-snug mb-8 uppercase">
            CONTRATO DE PRESTACION DE SERVICIOS DE TRANSPORTE EXCLUSIVO
            <br />
            INTERNACIONAL DE CARGA DE IMPORTACIONES
          </h1>

          {/* Body */}
          <p className="mb-4 indent-8">
            Conste por el presente documento privado, que con el solo
            reconocimiento de firmas y rubricas ante la autoridad competente
            surtirá los efectos de un documento público, un Contrato de
            prestación de Servicio de Transporte exclusivo internacional de
            carga, sujeto a los términos, clausulas y condiciones que a
            continuación se detallan:
          </p>

          {/* PRIMERA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">PRIMERA.- ( PARTES ).-</span>
            <p className="mb-3">
              <span className="font-bold">1. {companyName}</span>, legalmente
              constituida, con Matrícula de Comercio {companyFundempresa}, NIT
              No. {company.nit || "S/N"}, con domicilio{" "}
              {company.direccion || "S/N"}, representada legalmente por la Sra.{" "}
              {company.legalRepresentativeName || "S/N"}, mayor de edad, hábil
              por derecho, con C.I. No. {company.legalRepresentativeCI || "S/N"}
              , con domicilio en {company.domicilioLegal || "S/N"}, de la ciudad
              de {company.legalRepresentativeCIEmision || "S/N"} en calidad de
              apoderada, debidamente acreditada mediante escritura pública de
              testimonio de poder {companyTestimonio}, de fecha 2 de febrero de
              2025, ortorgado ante Notario de Fe Publica N-75 Dr. Jhonny Mena
              Soliz del distrito judicial de Cochabamba, que para los efectos
              del presente contrato se denominara{" "}
              <span className="font-bold">{companySigla}</span>.
            </p>
            <p>
              <span className="font-bold">
                2. LA EMPRESA{" "}
                {providerName || " [SELECCIONAR PROVEEDOR EN SUBASTA]"}
              </span>{" "}
              legalmente constituida, con Matrícula de Comercio No.{" "}
              {providerData.matricula_comercio || " [S/N]"}, NIT No.{" "}
              {providerData.nit_ruc || providerData.nit_tax_id || " [S/N]"}, con
              domicilio {providerData.direccion || " [S/D]"}, representada por
              el Sr. {providerData.representante_legal || " [S/R]"}, mayor de
              edad, habil por derecho, C.I. No.{" "}
              {providerData.ci_dni || " [S/N]"}, expedido en{" "}
              {providerData.ciudad || "CBBA"}, con domicilio en{" "}
              {providerData.dir_rep_legal || " [S/D]"}, en su calidad de
              Representante Legal tal cual se evidencia del testimonio No.{" "}
              {providerData.n_testimonio || "[Testimonio]"} de{" "}
              {providerData.fecha_testimonio || "[Fecha]"} otorgado ante{" "}
              {providerData.otorgado_ante || "[Notaria]"}, Notaría de fe pública
              No. {providerData.n_notaria || " [S/N]"} que, para efectos del
              presente contrato, se denominara LA EMPRESA.
            </p>
          </div>

          {/* SEGUNDA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              SEGUNDA.- (ANTECEDENTES).-
            </span>
            <p className="mb-3">
              <span className="font-bold">{companySigla}</span>, requiere la
              prestación de servicios de transporte exclusivo internacional de
              carga, para el traslado de{" "}
              {tipoImportacion || providerData.tipo_carga || "mercancía"}.
            </p>
            {renderInlineTextarea(
              "antecedentesContratoDeTransporte",
              "Describe detalladamente los antecedentes del traslado de mercancía...",
            )}
            <p className="mb-3">
              El precio acordado entre partes se sujeta a la cotización de
              trasporte presentada por{" "}
              <span className="font-bold">LA EMPRESA</span> y aprobada por{" "}
              <span className="font-bold">{companySigla}</span>, la cual no
              puede sufrir modificación o incremento alguno y forma parte
              integrante del presente contrato.
            </p>
            <p>
              <span className="font-bold">LA EMPRESA</span>, dedicada al rubro
              de Transporte de carga Internacional terrestre, declara que se
              encuentra capacitada para satisfacer las necesidades objeto del
              presente contrato a favor de{" "}
              <span className="font-bold">{companySigla}</span> y cumplir a
              cabalidad la prestación de servicios requeridos, en merito a
              contar con la experiencia debida en el rubro, de manera eficiente
              e idónea, conociendo y aceptando todas las particularidades del
              servicio mencionado.
            </p>
          </div>

          {/* TERCERA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">TERCERA.- (OBJETO).-</span>
            <p className="mb-3">
              Por medio del presente contrato,{" "}
              <span className="font-bold">LA EMPRESA</span> se obliga, a favor y
              satisfacción de <span className="font-bold">{companySigla}</span>,
              a prestar el "Servicio de Transporte exclusivo internacional de
              carga" en apego al presente contrato, para la importación
              identificada con el código {`${numRef} ${proveedor}`}. Este
              servicio contempla el traslado de {quantity}{" "}
              {quantity > 1 ? "contenedores" : "contenedor "}
              de {containerType}, como carga {modalidadDeCarga} -{" "}
              {descripcionGeneralDeLaCompra}, desde {puertoDescarga}-
              {paisDescarga}, hasta la planta de producción Santivañez, ubicado
              a 30km. de la ciudad de Cochabamba. El servicio se regirá por los
              términos y condiciones del presente contrato y la cotización
              adjunta.
            </p>
          </div>

          {/* CUARTA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              CUARTA.- (NATURALEZA DEL CONTRATO).-
            </span>
            <p className="mb-3">
              El presente contrato es suscrito de acuerdo a lo señalado por el
              Art 732 y siguientes, del Código Civil vigente, por lo que es de
              naturaleza estrictamente civil, sin que exista ninguna relación
              laboral entre los contratantes.
            </p>
            <p>
              Siendo <span className="font-bold">LA EMPRESA</span>, directa
              responsable por su personal al ser un servicio tercializado por
              parte de <span className="font-bold">{companySigla}</span>, por lo
              que <span className="font-bold">LA EMPRESA</span> debe asumir toda
              obligación laboral y/o beneficios sociales de cualquier índole en
              relación a su personal o contratados, se deja expresamente
              establecido, que el personal que contrate y ocupe{" "}
              <span className="font-bold">LA EMPRESA</span> a través suyo o
              mediante un subcontrato, para la realización del Servicio, no
              tendrá ninguna relación de dependencia obrero patronal con{" "}
              <span className="font-bold">{companySigla}</span>. debiendo en
              consecuencia <span className="font-bold">LA EMPRESA</span> correr
              con todos los gastos que demanden beneficios sociales, aporte al
              Segura Social a corto plaza, AFP y otros que la Ley exige al
              empleador, por lo que se excluye a{" "}
              <span className="font-bold">{companySigla}</span> de cualquier
              reclamo u otro de cualquier indole a este fin.
            </p>
          </div>

          {/* QUINTA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">QUINTA.- (PLAZO).-</span>
            <p className="mb-3">
              El plazo para realizar el transporte terrestre objeto de este
              contrato será de{" "}
              {renderInlineInput("diasPlazo", "Días", "number", "w-16")} días
              calendario, a partir de la fecha en que la carga haya sido
              debidamente cargada en el vehículo de transporte.{" "}
              <span className="font-bold">LA EMPRESA</span> otorgara{" "}
              {renderInlineInput("diasPlazoAduana", "Días", "number", "w-16")}{" "}
              días calendarios libres en Aduana correspondiente dentro el plazo
              máximo mencionado líneas arriba, con el fin de realizar los
              trámites de desaduanización.
            </p>
            <p>
              En caso de que <span className="font-bold">LA EMPRESA</span>, no
              cumpla con los plazos establecidos o exista algún impedimento para
              ello, <span className="font-bold">LA EMPRESA</span> tomara las
              medidas necesarias para asegurar el cumplimiento de dichos plazos,
              aunque esto implique costos adicionales con terceros, los cuales
              serán asumidos en su totalidad por la misma.
            </p>
          </div>

          {/* SEXTA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              SEXTA.- (CONTRAPRESTACION Y FORMA DE PAGO).-
            </span>
            <p className="mb-3">
              El precio final por la ejecución total y completa de los servicios
              de transporte por parte de{" "}
              <span className="font-bold">LA EMPRESA</span> de acuerdo a
              proforma aceptada, es de{" "}
              <span className="font-bold">
                $us{" "}
                {renderInlineInput("precioMonto", "Monto", "number", "w-28")} (
                {NumeroALetras(
                  Number(formValues.precioMonto) || 0,
                ).toUpperCase()}{" "}
                DÓLARES de los Estados Unidos de América)
              </span>
              .
            </p>
            <p className="mb-3">
              <span className="font-bold">{companySigla}</span>, se compromete a
              cancelar a <span className="font-bold">LA EMPRESA</span> la suma
              total pactada entre partes señalada líneas arriba una vez que{" "}
              <span className="font-bold">LA EMPRESA</span> haya entregado a{" "}
              <span className="font-bold">{companySigla}</span>, a su entera
              conformidad, toda la documentación detallada en el presente
              contrato.
            </p>
            <p className="mb-3">
              Las partes acuerdan que los montos mencionados serán transferidos
              a nombre de LA EMPRESA una vez sea descargado en almacén acordado.
              <br />
            </p>
            <p className="mb-3">
              Asimismo, LA EMPRESA declara que la contraprestación acordada
              incluye todos los costos directos e indirectos relacionados con la
              completa prestación de los servicios objeto del presente contrato,
              y que no estará sujeta a ningún incremento.
            </p>
            <p>
              Las partes acuerdan que los gastos de puerto, liberación y gastos
              en destino serán cubiertos por{" "}
              <span className="font-bold">LA EMPRESA</span>, y{" "}
              <span className="font-bold">{companySigla}</span> procederá a la
              devolución total de dichos gastos una vez entregados los
              correspondientes respaldos (los cuales deben ser consignados a
              nombre de{" "}
              <span className="font-bold">
                {companyName} NIT {company.nit || "1009109025"}
              </span>
              ).
            </p>
          </div>

          {/* SEPTIMA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              SEPTIMA.- (DOCUMENTOS INTEGRANTES).-
            </span>
            <p className="mb-3">
              Forman parte integrante e indivisible del presente Contrato, los
              siguientes documentos:
              <br />
              1.- Cotización de transporte.
              <br />
              2.- Copia Lista de Empaque.
              <br />
              3.- Copia Factura Comercial.
              <br />
              4.- B/L Original.
            </p>
            <p className="mb-3">
              Las partes acuerdan que estos documentos y todos aquellos que
              forman parte y son requeridos para el proceso de transporte objeto
              del presente contrato, tienen calidad de CONFIDENCIALIDAD, por lo
              que solo pueden ser usados o dispuestos para fines estrictamente
              de gestión o requerimiento legal del proceso de TRANSPORTE.
            </p>
            <p className="mb-3">
              Las partes acuerdan que, una vez entregada la carga en destino
              acordado, <span className="font-bold">LA EMPRESA</span> deberá
              entregar a <span className="font-bold">{companySigla}</span>, a
              conformidad, toda la documentación correspondiente y detallada a
              continuación, en un plazo máximo de un día hábil a partir de la
              fecha de entrega, con el fin de proceder con la regularización
              correspondiente ante Aduana.
            </p>
            <p>
              Los documentos requeridos son las siguientes:
              <br />
              1.- MIC/DTA- Manifiesto Internacional de Carga
              <br />
              2.- CRT - Carta de Porte Internacional por Carretera
              <br />
              3.- Factura de Transporte Terrestre Internacional
              <br />
              4.- Nota de Cobranza
              <br />
              5.- Comprobantes de pagos realizados para la liberación
              <br />
              <br />
              Todos los comprobantes y documentos deben estar emitidos a nombre
              de {companyName}.
            </p>
          </div>

          {/* OCTAVA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              OCTAVA.- (REGIMEN POR INCUMPLIMIENTO).-
            </span>
            <p>
              En caso de que <span className="font-bold">LA EMPRESA</span> no
              cumpla con la programación fijada o las plazas establecidas para
              la prestación de los servicios y/o documentación, objeto del
              presente contrato, por razones atribuibles a su servicio, que no
              sean caso fortuito o fuerza mayor debidamente comprobable,{" "}
              <span className="font-bold">LA EMPRESA</span> reconocerá a{" "}
              <span className="font-bold">{companySigla}</span> el 5% del valor
              del contrato por cada día de retraso, además del resarcimiento de
              daños y perjuicios ocasionados por el incumplimiento. Las partes
              acuerdan que <span className="font-bold">{companySigla}</span>{" "}
              podrá retener de las pagos que adeude a LA EMPRESA por el 5% del
              total del contrato y/o el resarcimiento de daños, si las hubiere.
            </p>
          </div>

          {/* NOVENA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              NOVENA.- (REPRESENTANTE DE {companySigla}).-
            </span>
            <p>
              {companySigla}, designa como responsable para el control,
              verificación y recepción de la carga al Sr.{" "}
              <span className="font-bold border-b-2 border-dashed border-amber-400 px-2">
                {formValues.responsableDeAlmacen || "Pendiente de asignación"}
              </span>{" "}
              responsable de{" "}
              <span className="font-bold">
                {dataValues?.["almacénDestino"] || "(Almacén no definido)"}
              </span>
              , siendo el indicado para la recepción de la carga y
              documentación.
            </p>
          </div>

          {/* DECIMA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA.- (SUBCONTRATACIÓN Y RESERVA DE DERECHOS).-
            </span>
            <p>
              <span className="font-bold">LA EMPRESA</span> se obliga a
              notificar a {companySigla} sobre cualquier subcontratación de los
              servicios previstos en el presente Contrato, asumiendo plena
              responsabilidad por las consecuencias derivadas de dicha
              subcontratación, incluyendo los riesgos asociados al cumplimiento
              de sus propias obligaciones. Asimismo, deberá proporcionar a{" "}
              <span className="font-bold">{companySigla}</span> una copia del
              contrato suscrito con el subcontratista, para efectos de
              conocimiento.
            </p>
          </div>

          {/* DECIMA PRIMERA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA PRIMERA. - (OBLIGACIONES DE LA EMPRESA).-
            </span>
            <p className="mb-2">
              Para el cumplimiento del objeto del presente Contrato,{" "}
              <span className="font-bold">LA EMPRESA</span> se obliga a:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Asumir plena responsabilidad por la prestación de los servicios
                conforme al alcance previsto en este Contrato y sus Anexos.
              </li>
              <li>
                Solicitar por escrito y con la debida anticipación cualquier
                información o documentación requerida a{" "}
                <span className="font-bold">{companySigla}</span>, evitando
                demoras o contratiempos.
              </li>
              <li>
                Garantizar que sus dependientes respeten la confidencialidad de
                la documentación relacionada con el proceso de transporte.
              </li>
              <li>
                Entregar puntualmente toda la documentación necesaria para los
                trámites de desaduanización y entrega final.
              </li>
              <li>
                Cumplir estrictamente con los plazos establecidos en el presente
                Contrato.
              </li>
              <li>
                Entregar los documentos originales y copias pertinentes al
                responsable o analista de Importaciones de{" "}
                <span className="font-bold">{companySigla}</span>.
              </li>
              <li>
                Responder por los daños ocasionados en la prestación del
                servicio.
              </li>
              <li>
                Cumplir con todas las disposiciones del presente Contrato.
              </li>
              <li>
                Cumplir con la ruta acordada, estableciendo tiempos de
                recorrido, puntos de control, áreas de descanso y servicios
                clave.
              </li>
              <li>
                Transportar la carga de manera exclusiva, incluso si existen
                espacios libres.
              </li>
              <li>Devolver los contenedores dentro del plazo acordado.</li>
              <li>
                Asegurar que los conductores cuenten con agendas telefónicas
                actualizadas, incluyendo contactos de módulos policiales en sus
                rutas, transportistas cercanos, grúas, talleres, ambulancias y
                centros de salud.
              </li>
            </ul>
          </div>

          {/* DECIMA SEGUNDA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA SEGUNDA. - (SEGURIDAD EN EL TRANSPORTE DE LA MERCANCÍA).-
            </span>
            <p className="mb-3">
              La empresa de transporte se compromete a implementar y mantener
              medidas de seguridad estrictas para garantizar la integridad de la
              carga durante todo el proceso de traslado, cumpliendo con los
              siguientes requisitos:
            </p>
            <p className="mb-3">
              Las unidades de transporte deberán contar con un Sistema de
              Posicionamiento Global (GPS), o un medio de localización
              equivalente, asegurando el resguardo de los datos de control por
              un mínimo de un (1) año.
            </p>
            <p className="mb-3">
              El transportista autoriza a{" "}
              <span className="font-bold">{companySigla}</span> a realizar
              auditorías de segunda parte cuando lo considere necesario, a fin
              de verificar el cumplimiento de las condiciones pactadas en el
              presente contrato.
            </p>
            <p className="mb-3">
              Se deberán implementar mecanismos que restrinjan el acceso al
              medio y unidad de transporte, permitiendo el ingreso únicamente al
              conductor y, opcionalmente, a un relevo, quien deberá cumplir con
              las mismas condiciones de seguridad exigidas al conductor.
            </p>
            <p className="mb-3">
              <span className="font-bold">LA EMPRESA</span>, deberá contar con
              la documentación propietaria u otros, de las unidades y permisos
              de viabilidad dentro y fuera del territorio nacional, debidamente
              saneados y al día.
            </p>
            <p className="mb-3">
              <span className="font-bold">LA EMPRESA</span>, debe contar con las
              pólizas de seguro pertinentes tanto de la unidad de transporte
              como de los choferes u acompañantes, siendo responsabilidad
              directa de <span className="font-bold">LA EMPRESA</span> el
              personal que presta el servicio de transporte u otros afines
              requeridos para el cumplimiento del objeto del presente contrato.
            </p>
            <p>
              <span className="font-bold">LA EMPRESA</span>, no puede realizar
              bajo ningún motivo el traslado o cambio de la carga a otras
              unidades de transporte, siendo una prohibición esta condición
              dentro del presente contrato.
            </p>
          </div>

          {/* DECIMA TERCERA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA TERCERA.- (SEGURIDAD DE LOS MEDIOS Y UNIDADES DE
              TRANSPORTE).-
            </span>
            <p>
              Las unidades de transporte en espera antes de la descarga deberán
              permanecer en áreas seguras y monitoreadas. Asimismo, deberán
              estar en óptimas condiciones y contar con la revisión técnica,
              mantenimiento correspondiente, SOAT e inspección técnica
              vehicular, según corresponda.
            </p>
          </div>

          {/* DECIMA CUARTA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA CUARTA.- (OBLIGACIONES DE {companySigla}).-
            </span>
            <p className="mb-2">
              Son, por otra parte, obligaciones de{" "}
              <span className="font-bold">{companySigla}</span>:
            </p>
            <ul className="list-disc pl-6 space-y-1 mt-1">
              <li>
                Proporcionar la información y los recursos necesarios para la
                correcta prestación de los servicios contratados.
              </li>
              <li>
                Efectuar el pago acordado a{" "}
                <span className="font-bold">LA EMPRESA</span> una vez recibida y
                validada toda la documentación correspondiente.
              </li>
              <li>
                Contratar, por su cuenta, un seguro Todo Riesgo para la carga.
              </li>
              <li>
                Cumplir con todas las disposiciones establecidas en el presente
                contrato.
              </li>
            </ul>
          </div>

          {/* DECIMA QUINTA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA QUINTA.- (FUERZA MAYOR).-
            </span>
            <p className="mb-3">
              Ni <span className="font-bold">LA EMPRESA</span>, ni{" "}
              <span className="font-bold">{companySigla}</span>, deberán
              responsabilizarse el uno al otro, o considerarse en violación del
              Contrato, en razón a un atraso o incumplimiento en la ejecución de
              cualquiera de sus obligaciones en la medida y por el plazo en que
              el atraso o la imposibilidad de cumplimiento se deban a un evento
              de Fuerza Mayor.
            </p>
            <p className="mb-3">
              Para los efectos de este contrato, "Fuerza Mayor" se entenderá
              como cualquier acontecimiento, acto o evento fuera del control de
              la parte obligada, imprevisible o inevitable, incluso tras haber
              adoptado las precauciones necesarias.
            </p>
            <p className="mb-3">
              Constituirán Fuerza Mayor, entre otros que se adecuen a la
              precedente, los siguientes actos o eventos: Fenómenos de la
              naturaleza de carácter extraordinario, guerras (declaradas o no),
              actos de terrorismo, bloqueos, disturbios civiles, motines,
              insurrecciones, sabotajes, cuarentenas y epidemias. Actos de una
              autoridad gubernamental que no hayan sido inducidos
              voluntariamente o promovidos por la parte afectada, ni resulten de
              su incumplimiento contractual. Imposibilidad de obtener, a tiempo
              y pese a los mejores esfuerzos, permisos o licencias necesarias
              según este contrato, excepto aquellos requeridos para el personal
              y subcontratistas del contratista.
            </p>
            <p>
              Cuando <span className="font-bold">LA EMPRESA</span> se viera
              afectada por un evento de fuerza mayor y no notificara a{" "}
              <span className="font-bold">{companySigla}</span> dentro del plazo
              de 24 hrs impostergablemente, perderá el derecho de que dicho
              evento lo excuse o excluya de su responsabilidad para la correcta
              y oportuna prestación de los servicios, así como todo otro derecho
              que desde esta Clausula le asista.
            </p>
          </div>

          {/* DECIMA SEXTA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA SEXTA. - (EXIGIBILIDAD DEL CONTRATO).-
            </span>
            <p>
              Las obligaciones de las partes establecidas en este Contrato serán
              exigibles sin necesidad de constitución en mora ni citación previa
              alguna, misma a la que las partes renuncian expresamente.
            </p>
          </div>

          {/* DECIMA SEPTIMA */}
          <div className="mb-6">
            <span className="font-bold block mb-1">
              DECIMA SEPTIMA (CONFORMIDAD).-
            </span>
            <p>
              En señal de conformidad y aceptación con todas y cada una de las
              cláusulas insertas en el presente instrumento, las partes
              intervinientes suscriben en triple ejemplar con un mismo tenor.
            </p>
          </div>

          {/* Notary Location & Date */}
          <div className="text-center my-10 font-bold">
            {renderInlineInput(
              "cuidadDeLaNotaria",
              "Ciudad de la Notaría",
              "text",
              "w-36",
            )}
            , {formatearFecha(formValues.fechaContrato)}
            <div className="inline-block ml-2 align-middle">
              <input
                type="date"
                value={formValues.fechaContrato || ""}
                onChange={(e) => handleChange("fechaContrato", e.target.value)}
                className="border border-gray-300 rounded px-2 py-0.5 ml-2 font-normal text-xs text-gray-700 bg-white inline-block focus:border-blue-500 outline-none cursor-pointer"
              />
            </div>
          </div>

          {/* Signature Blocks */}
          <div className="flex justify-between mt-16 px-6">
            <div className="w-[45%] text-left">
              <div className="h-24 flex items-end justify-center mb-1">
                {legalRepresentativeSignatureSrc ? (
                  <img
                    src={legalRepresentativeSignatureSrc}
                    alt={`Firma de ${company.legalRepresentativeName || "representante legal"}`}
                    className="max-h-24 max-w-[300px] object-contain"
                  />
                ) : null}
              </div>
              <div className="border-t border-gray-400 pt-2">
                <div className="font-bold text-[13px] text-gray-900">
                  {/*   {company.legalRepresentativeName || "S/N"} */}
                  Gabriela Miranda Garcia
                </div>
                <div className="text-[12px] text-gray-500">
                  C.I. {/* {company.legalRepresentativeCI || "S/N"} */}
                  4298322
                </div>
                <div className="font-bold text-[11px] text-amber-600 mt-1 uppercase">
                  {/* {companyName} */}
                  Responsable De Importaciones
                </div>
              </div>
            </div>
            <div className="w-[45%] text-left">
              <div className="h-24 mb-1" />
              <div className="border-t border-gray-400 pt-2">
                <div className="font-bold text-[13px] text-gray-900">
                  {providerData.representante_legal || "Juan Carlos Rios Coca"}
                </div>
                <div className="text-[12px] text-gray-500">
                  C.I. {providerData.ci_dni || "4427058."}
                </div>
                <div className="font-bold text-[11px] text-amber-600 mt-1 uppercase">
                  REPRESENTANTE LEGAL {providerName || "LA EMPRESA"}.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
